// App.js - Complete fix with proper routing and state management
import React from 'react'
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom'
import { AuthProvider, useAuth } from './contexts/AuthContext'
import Layout from './components/Layout'
import Login from './pages/Login'
import Privacy from './pages/Privacy'
import Terms from './pages/Terms'
import Landing from './pages/Landing'
import Dashboard from './pages/Dashboard'
import PasswordReset from './pages/PasswordReset'
import CallLogs from './pages/CallLogs'
import Businesses from './pages/Businesses'
import AdminUsers from './pages/AdminUsers'
import MyBusiness from './pages/MyBusiness'
import Settings from './pages/Settings'
import OAuthCallback from './components/OAuthCallback'
import ProtectedRoute from './components/ProtectedRoute'
import Customers from './pages/Customers'
import PlumbersLanding from './pages/plumbers'
import ElectriciansLanding from './pages/electricians'
import HvacLanding from './pages/hvac'
import LocksmithsLanding from './pages/locksmiths'
import ContractorsLanding from './pages/contractors'
import RoofersLanding from './pages/roofers'
import LandscapersLanding from './pages/landscapers'
import RealEstateLanding from './pages/realestate'
import TaxPrepLanding from './pages/taxprep'
import CleaningLanding from './pages/cleaning'
import AutoRepairLanding from './pages/autorepair'
import LawFirmsLanding from './pages/lawfirms'
import MedicalLanding from './pages/medical'
import InsuranceLanding from './pages/insurance'
import MedSpaLanding from './pages/medspa'
import NailSalonLanding from './pages/nailsalon'
import HairSalonLanding from './pages/hairsalon'
import VeterinarianLanding from './pages/veterinarian'
import TherapistLanding from './pages/therapist'
import MassageLanding from './pages/massage'
import ChiropractorLanding from './pages/chiropractor'
import DentistLanding from './pages/dentist'
import PhysicalTherapyLanding from './pages/physicaltherapy'
import FitnessLanding from './pages/fitness'
import TutoringLanding from './pages/tutoring'
import PetGroomingLanding from './pages/petgrooming'
import GymLanding from './pages/gym'
import IndustriesPage from './pages/industries'
import BlogIndex from './pages/blog/index'
import AIReceptionistsSmallBusiness from './pages/blog/ai-receptionists-small-business'
import AIReceptionistsLawFirms from './pages/blog/ai-receptionists-law-firms'
import AIReceptionistsPersonalInjuryLaw from './pages/blog/ai-receptionists-personal-injury-law'
import AIReceptionistsFamilyLaw from './pages/blog/ai-receptionists-family-law'
import AIReceptionistsCriminalDefense from './pages/blog/ai-receptionists-criminal-defense'
import AIReceptionistsEstatePlanning from './pages/blog/ai-receptionists-estate-planning'
import AIReceptionistsHVACCompanies from './pages/blog/ai-receptionists-hvac-companies'
import AIReceptionistsPlumbingServices from './pages/blog/ai-receptionists-plumbing-services'
import AIReceptionistsElectricalContractors from './pages/blog/ai-receptionists-electrical-contractors'
import AIReceptionistsMedicalPractices from './pages/blog/ai-receptionists-medical-practices'
import AIReceptionistsRealEstate from './pages/blog/ai-receptionists-real-estate'
import AIReceptionistsAutoRepair from './pages/blog/ai-receptionists-auto-repair'
import AIReceptionistsHomeServices from './pages/blog/ai-receptionists-home-services'
import AIVsTraditionalReceptionist from './pages/ai-vs-traditional-receptionist'
import AIVsVirtualAssistant from './pages/ai-vs-virtual-assistant'
import AIVsCallCenter from './pages/ai-vs-call-center'
import EmergencyServices from './pages/emergency-services'
import AppointmentBooking from './pages/appointment-booking'
import LeadGeneration from './pages/lead-generation'
import TwentyFourSevenPhoneAnswering from './pages/24-7-phone-answering'
import BilingualAIReceptionist from './pages/bilingual-ai-receptionist'
import VirtualReceptionistsServiceBusinesses from './pages/blog/virtual-receptionists-service-businesses'
import ROIAiPhoneSystems from './pages/blog/roi-ai-phone-systems'
import TwentyFourSevenCustomerService from './pages/blog/24-7-customer-service-importance'
import GarageDoorLanding from './pages/garagedoor'
import ApplianceRepairLanding from './pages/appliance-repair'
import PestControlLanding from './pages/pest-control'
import DuctGutterCleaningLanding from './pages/duct-gutter-cleaning'
import WaterDamageRestorationLanding from './pages/water-damage-restoration'
import TireShopsLanding from './pages/tire-shops'
import OilChangeLubeLanding from './pages/oil-change-lube'
import BodyShopsLanding from './pages/body-shops'
import GlassRepairTintingLanding from './pages/glass-repair-tinting'
import PropertyManagementLanding from './pages/property-management'
import RealEstateBrokeragesLanding from './pages/real-estate-brokerages'
import RevenueAnalytics from './pages/RevenueAnalytics'
import RubyReceptionistsAlternative from './pages/ruby-receptionists-alternative'
import MissedCallsCostCalculator from './pages/missed-calls-cost-calculator'
import AIVsAnsweringService from './pages/ai-vs-answering-service'
import AfterHoursEmergencyCalls from './pages/after-hours-emergency-calls'
import SeasonalBusinessPhoneCoverage from './pages/seasonal-business-phone-coverage'


// Simple loading component
function LoadingSpinner() {
  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center">
      <div className="text-center">
        <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600 mx-auto mb-4"></div>
        <p className="text-gray-600">Loading application...</p>
      </div>
    </div>
  )
}

// Protected layout wrapper
function ProtectedLayout({ children }) {
  return (
    <ProtectedRoute>
      <Layout>
        {children}
      </Layout>
    </ProtectedRoute>
  )
}

// Super admin route wrapper
function SuperAdminRoute({ children }) {
  return (
    <ProtectedRoute requireSuperAdmin={true}>
      <Layout>
        {children}
      </Layout>
    </ProtectedRoute>
  )
}

// Main app content with routing
function AppContent() {
  const { user, loading } = useAuth()

  console.log('🎯 AppContent render:', { loading, hasUser: !!user, userEmail: user?.email })

  // Show loading while determining auth state
  if (loading) {
    return <LoadingSpinner />
  }

  return (
    <Routes>
      <Route path="/oauth/callback" element={<OAuthCallback />} />

      {/* Public routes */}
      <Route 
        path="/" 
        element={user ? <Navigate to="/dashboard" replace /> : <Landing />} 
      />
      <Route 
        path="/login" 
        element={user ? <Navigate to="/dashboard" replace /> : <Login />} 
      />
            <Route 
        path="/terms" 
        element={<Terms />} 
      />
                  <Route 
        path="/privacy" 
        element={<Privacy />} 
      />
      <Route 
        path="/reset-password" 
        element={user ? <Navigate to="/dashboard" replace /> : <PasswordReset />} 
      />
      
      {/* Trade-specific landing pages */}
      <Route path="/plumbers" element={<PlumbersLanding />} />
      <Route path="/electricians" element={<ElectriciansLanding />} />
      <Route path="/hvac" element={<HvacLanding />} />
      <Route path="/locksmiths" element={<LocksmithsLanding />} />
      <Route path="/contractors" element={<ContractorsLanding />} />
      <Route path="/roofers" element={<RoofersLanding />} />
      <Route path="/landscapers" element={<LandscapersLanding />} />
      <Route path="/realestate" element={<RealEstateLanding />} />
      <Route path="/taxprep" element={<TaxPrepLanding />} />
      <Route path="/cleaning" element={<CleaningLanding />} />
      <Route path="/autorepair" element={<AutoRepairLanding />} />
      <Route path="/lawfirms" element={<LawFirmsLanding />} />
      <Route path="/medical" element={<MedicalLanding />} />
      <Route path="/insurance" element={<InsuranceLanding />} />
      <Route path="/medspa" element={<MedSpaLanding />} />
      <Route path="/nailsalon" element={<NailSalonLanding />} />
      <Route path="/hairsalon" element={<HairSalonLanding />} />
      <Route path="/veterinarian" element={<VeterinarianLanding />} />
      <Route path="/therapist" element={<TherapistLanding />} />
      <Route path="/massage" element={<MassageLanding />} />
      <Route path="/chiropractor" element={<ChiropractorLanding />} />
      <Route path="/dentist" element={<DentistLanding />} />
      <Route path="/physicaltherapy" element={<PhysicalTherapyLanding />} />
      <Route path="/fitness" element={<FitnessLanding />} />
      <Route path="/tutoring" element={<TutoringLanding />} />
      <Route path="/petgrooming" element={<PetGroomingLanding />} />
      <Route path="/gym" element={<GymLanding />} />
      <Route path="/industries" element={<IndustriesPage />} />
      
      {/* Blog routes */}
      <Route path="/blog" element={<BlogIndex />} />
      <Route path="/blog/ai-receptionists-small-business" element={<AIReceptionistsSmallBusiness />} />
      <Route path="/blog/ai-receptionists-law-firms" element={<AIReceptionistsLawFirms />} />
      <Route path="/blog/ai-receptionists-personal-injury-law" element={<AIReceptionistsPersonalInjuryLaw />} />
      <Route path="/blog/ai-receptionists-family-law" element={<AIReceptionistsFamilyLaw />} />
      <Route path="/blog/ai-receptionists-criminal-defense" element={<AIReceptionistsCriminalDefense />} />
      <Route path="/blog/ai-receptionists-estate-planning" element={<AIReceptionistsEstatePlanning />} />
      <Route path="/blog/ai-receptionists-hvac-companies" element={<AIReceptionistsHVACCompanies />} />
      <Route path="/blog/ai-receptionists-plumbing-services" element={<AIReceptionistsPlumbingServices />} />
      <Route path="/blog/ai-receptionists-electrical-contractors" element={<AIReceptionistsElectricalContractors />} />
      <Route path="/blog/ai-receptionists-medical-practices" element={<AIReceptionistsMedicalPractices />} />
      <Route path="/blog/ai-receptionists-real-estate" element={<AIReceptionistsRealEstate />} />
      <Route path="/blog/ai-receptionists-auto-repair" element={<AIReceptionistsAutoRepair />} />
      <Route path="/blog/ai-receptionists-home-services" element={<AIReceptionistsHomeServices />} />
      <Route path="/blog/virtual-receptionists-service-businesses" element={<VirtualReceptionistsServiceBusinesses />} />
      <Route path="/blog/roi-ai-phone-systems" element={<ROIAiPhoneSystems />} />
      <Route path="/blog/24-7-customer-service-importance" element={<TwentyFourSevenCustomerService />} />
      
      {/* SEO Comparison Pages */}
      <Route path="/ai-vs-traditional-receptionist" element={<AIVsTraditionalReceptionist />} />
      <Route path="/ai-vs-virtual-assistant" element={<AIVsVirtualAssistant />} />
      <Route path="/ai-vs-call-center" element={<AIVsCallCenter />} />
      
      {/* SEO Use Case Pages */}
      <Route path="/emergency-services" element={<EmergencyServices />} />
      <Route path="/appointment-booking" element={<AppointmentBooking />} />
      <Route path="/lead-generation" element={<LeadGeneration />} />
      
      {/* SEO Feature Pages */}
      <Route path="/24-7-phone-answering" element={<TwentyFourSevenPhoneAnswering />} />
      <Route path="/bilingual-ai-receptionist" element={<BilingualAIReceptionist />} />
      
      {/* New SEO Pages */}
      <Route path="/ruby-receptionists-alternative" element={<RubyReceptionistsAlternative />} />
      <Route path="/missed-calls-cost-calculator" element={<MissedCallsCostCalculator />} />
      <Route path="/ai-vs-answering-service" element={<AIVsAnsweringService />} />
      <Route path="/after-hours-emergency-calls" element={<AfterHoursEmergencyCalls />} />
      <Route path="/seasonal-business-phone-coverage" element={<SeasonalBusinessPhoneCoverage />} />
      
      {/* Additional Business Pages */}
      <Route path="/garagedoor" element={<GarageDoorLanding />} />
      <Route path="/appliance-repair" element={<ApplianceRepairLanding />} />
      <Route path="/pest-control" element={<PestControlLanding />} />
      <Route path="/duct-gutter-cleaning" element={<DuctGutterCleaningLanding />} />
      <Route path="/water-damage-restoration" element={<WaterDamageRestorationLanding />} />
      <Route path="/tire-shops" element={<TireShopsLanding />} />
      <Route path="/oil-change-lube" element={<OilChangeLubeLanding />} />
      <Route path="/body-shops" element={<BodyShopsLanding />} />
      <Route path="/glass-repair-tinting" element={<GlassRepairTintingLanding />} />
      <Route path="/property-management" element={<PropertyManagementLanding />} />
      <Route path="/real-estate-brokerages" element={<RealEstateBrokeragesLanding />} />
    
      
      {/* Protected routes for all authenticated users */}
      <Route 
        path="/dashboard" 
        element={
          <ProtectedLayout>
            <Dashboard />
          </ProtectedLayout>
        } 
      />

<Route 
        path="/customers" 
        element={
          <ProtectedLayout>
            <Customers />
          </ProtectedLayout>
        } 
      />
      
      <Route 
        path="/calls" 
        element={
          <ProtectedLayout>
            <CallLogs />
          </ProtectedLayout>
        } 
      />

    <Route 
      path="/revenue" 
      element={
        <ProtectedLayout>
          <RevenueAnalytics />
        </ProtectedLayout>
      } 
    />
      
      <Route 
        path="/business" 
        element={
          <ProtectedLayout>
            <MyBusiness />
          </ProtectedLayout>
        } 
      />
      
    
      
      <Route 
        path="/settings" 
        element={
          <ProtectedLayout>
            <Settings />
          </ProtectedLayout>
        } 
      />
      
      {/* Super admin only routes */}
      <Route 
        path="/businesses" 
        element={
          <SuperAdminRoute>
            <Businesses />
          </SuperAdminRoute>
        } 
      />
      
      <Route 
        path="/admin/users" 
        element={
          <SuperAdminRoute>
            <AdminUsers />
          </SuperAdminRoute>
        } 
      />
      
      {/* Catch all - redirect to dashboard if logged in, landing if not */}
      <Route 
        path="*" 
        element={
          user ? <Navigate to="/dashboard" replace /> : <Navigate to="/" replace />
        } 
      />


    </Routes>
  )
}

// Root App component
function App() {
  console.log('🚀 App component mounting')
  
  return (
    <div className="App">
      <Router>
        <AuthProvider>
          <AppContent />
        </AuthProvider>
      </Router>
    </div>
  )
}

export default App